// What the provider filter says about itself when it is collapsed.
//
// The filter moved out of a 200px sidebar column — which stood mostly empty
// beside two checkboxes and took that width away from the calendar — and into
// the toolbar next to Status. Collapsed, it has one line to say who is being
// shown, so that line has to be true.

export interface FilterableProvider {
  id: string;
  name: string;
}

/**
 * A one-line summary of who the calendar is showing.
 *
 * Ticking nothing means nobody — the checkboxes mean what they look like they
 * mean. The one case that is not a selection at all is having no providers to
 * tick yet: during the first fetch, or after it fails, the calendar is not
 * filtering and the control should not claim otherwise.
 *
 * Ticked ids that match no known provider are ignored — a stale ?provider=
 * value must not be counted as a selection nobody can see or clear.
 */
export function summarizeProviderSelection(
  providers: FilterableProvider[],
  selectedIds: string[],
): string {
  if (providers.length === 0) return 'All providers';

  const known = providers.filter((provider) => selectedIds.includes(provider.id));

  if (known.length === 0) return 'No providers';
  if (known.length === providers.length) return 'All providers';
  if (known.length === 1) return known[0].name;
  return `${known.length} of ${providers.length} providers`;
}

/**
 * Whether an appointment survives the provider filter.
 *
 * The ticked set is the set being shown, so ticking nothing hides everything.
 * Two things are deliberately not treated as a selection:
 *
 * - No providers to tick. selectedIds is empty during the first fetch and
 *   stays empty if that fetch fails, and blanking the calendar there would be
 *   indistinguishable from a day with nothing booked.
 * - An appointment nobody is named on, or named on somebody who is not on the
 *   list. It still blocks real time, so it stays visible as long as the filter
 *   is not hiding everything.
 */
export function matchesProviderFilter(
  providers: FilterableProvider[],
  selectedIds: string[],
  appointmentProvider?: string | null,
): boolean {
  if (providers.length === 0) return true;
  if (selectedIds.length === 0) return false;
  if (!appointmentProvider) return true;

  const named = appointmentProvider.toLowerCase();
  const match = providers.find(
    (provider) => provider.name.toLowerCase() === named || provider.id === appointmentProvider,
  );

  if (!match) return true;
  return selectedIds.includes(match.id);
}
